import { cachedFetch } from './cache';

export const FATWA_API       = '/api/fatwas';
export const ARTICLE_API     = '/api/articles';
export const RESEARCHERS_API = '/api/researchers';
export const LECTURES_API    = '/api/lectures';
export const RESEARCH_API    = '/api/research-papers';
export const CATEGORIES_API  = '/api/categories';
export const STATS_API       = '/api/stats';
export const CONTACT_API     = '/api/contact';
export const STORAGE_BASE    = '/storage/';

export { cachedFetch };

export const SCHOLAR_COLORS = [
  'linear-gradient(160deg,#0a1f14,#1b4332)',
  'linear-gradient(160deg,#1a2a4a,#2a4a7a)',
  'linear-gradient(160deg,#2a1a0a,#5a3a1a)',
  'linear-gradient(160deg,#3a1a1a,#7a3020)',
  'linear-gradient(160deg,#0a1a2a,#1a3a5a)',
  'linear-gradient(160deg,#1a0a2a,#3a1a5a)',
];

export const LECTURE_GRADIENTS = [
  'linear-gradient(135deg,#0a1f14,#1b4332)',
  'linear-gradient(135deg,#1a2a3a,#2a4a6a)',
  'linear-gradient(135deg,#2a1a0a,#5a3a1a)',
  'linear-gradient(135deg,#3a1a1a,#7a3020)',
  'linear-gradient(135deg,#1a3a1a,#2a6a30)',
  'linear-gradient(135deg,#1a2a4a,#304a7a)',
];

export const CAT_ICONS = {
  fiqh:'⚖️',tafsir:'📖',quran:'🕌',philosophy:'🌿',
  aqida:'🌿',history:'🏛️',hadith:'📜',seerah:'🕋',akhlaq:'💎',
  'الفقه والأصول':'⚖️','التفسير واللغة':'📖','الفلسفة والعقيدة':'🌿',
  'علوم القرآن':'🕌','التاريخ والحضارة':'🏛️',
};

export function stripHtml(h=''){const d=document.createElement('div');d.innerHTML=h;return d.textContent||d.innerText||'';}
export function trunc(str,n=130){const s=stripHtml(str||'');return s.length>n?s.slice(0,n).trimEnd()+'…':s;}
export function fatwaNum(f){if(f.code){const m=f.code.match(/[فعتخق]\d+/);if(m)return m[0];}return '';}
export function catIcon(catName='',categories=[]){const cat=categories.find(x=>x.name_ar===catName||x.name_en===catName);if(cat&&CAT_ICONS[cat.name_en])return CAT_ICONS[cat.name_en];return CAT_ICONS[catName]||'📜';}
export function scholarInitial(name=''){const words=(name||'').replace(/\s+/,' ').trim().split(' ').filter(Boolean);return(words[words.length-1]||words[0]||'م').charAt(0);}
// Strip a leading honorific in any supported language so we never double it up.
export function scholarShortName(name=''){return name.replace(/^(?:الدكتور|الأستاذ|الشيخ|دكتور|أستاذ|د\.|أ\.|ڈاکٹر|پروفیسر|استاد|استاذ|شیخ|جناب|Dr\.?|Prof(?:essor)?\.?|Sheikh|Ustadh|Mr\.?)\s*/i,'').trim()||name;}
// Scholars who carry the "دكتور" title; everyone else is "الأستاذ".
const DR_SCHOLARS=[/خالد\s*نصر/,/محمد\s*نور/,/khaled\s*nasr/i,/mohamed\s*nour/i,/muhammad\s*nour/i];
// Localised honorifics per language.
const HONORIFICS={
  dr:   {ar:'دكتور',   en:'Dr.',       ur:'ڈاکٹر'},
  prof: {ar:'الأستاذ', en:'Professor', ur:'پروفیسر'},
};
export function scholarHonorific(name='', lang='ar'){const h=HONORIFICS[DR_SCHOLARS.some(re=>re.test(name))?'dr':'prof'];return h[lang]||h.ar;}
export function scholarTitledName(name='', lang='ar'){const bare=scholarShortName(name);if(!bare)return '';return `${scholarHonorific(name,lang)} ${bare}`;}
// Display order: خالد نصر first, محمد نور second, then everyone else.
export function scholarRank(s){const name=(s&&s.name)||(typeof s==='string'?s:'')||'';if(/خالد\s*نصر|khaled\s*nasr/i.test(name))return 0;if(/محمد\s*نور|mohamed\s*nour|muhammad\s*nour/i.test(name))return 1;return 2;}
export function sortScholars(list=[]){return [...(list||[])].sort((a,b)=>scholarRank(a)-scholarRank(b));}

// Return the localised name/role/bio of a scholar object based on current lang.
// Falls back: versions[lang] → root field (Arabic).
export function scholarLocal(s, lang){
  if(!s) return {name:'', role:'', bio:'', cv_full_text:''};
  const v=(s.versions||{})[lang];
  if(v && v.name){
    return {
      name:         v.name         || s.name         || '',
      role:         v.role         || s.role         || '',
      bio:          v.bio          || s.bio          || '',
      cv_full_text: v.cv_full_text || s.cv_full_text || '',
    };
  }
  return {
    name:         s.name         || '',
    role:         s.role         || '',
    bio:          s.bio          || '',
    cv_full_text: s.cv_full_text || '',
  };
}
export function scholarLocalName(s, lang){ return scholarLocal(s, lang).name; }

// Given a fatwa (which has researcher_id and the Arabic name in f.scholar),
// return the localised scholar name. Tries in order:
// 1) f.scholar_names[lang] (sent by the API)
// 2) Look up the scholar in the scholars list by researcher_id / name and use its versions[lang]
// 3) Fall back to f.scholar (Arabic root name)
export function fatwaScholarName(f, scholars, lang, fallback=''){
  if(!f) return fallback;
  // 1) Use scholar_names map from API if available
  if(f.scholar_names && f.scholar_names[lang]) return f.scholar_names[lang];
  // 2) Look up in the scholars list
  const s = (scholars||[]).find(x =>
    (f.researcher_id && x.id === f.researcher_id) ||
    (f.scholar && x.name === f.scholar)
  );
  if(s) return scholarLocalName(s, lang) || s.name || fallback;
  // 3) Final fallbacks: any other entry in scholar_names, then root scholar name
  if(f.scholar_names){
    if(f.scholar_names.ar) return f.scholar_names.ar;
    const keys=Object.keys(f.scholar_names);
    if(keys.length) return f.scholar_names[keys[0]];
  }
  return f.scholar || fallback;
}
export function articleImg(a){const raw=a.featured_image||a.image||'';if(!raw)return '';return raw.startsWith('http')?raw:STORAGE_BASE+raw;}
export function lectureImg(l){if(!l.cover_image)return '';return l.cover_image.startsWith('http')?l.cover_image:STORAGE_BASE+l.cover_image;}
export function lectureFileUrl(l){if(!l.file_url)return '';return l.file_url.startsWith('http')?l.file_url:STORAGE_BASE+l.file_url;}
export function articleDate(a, lang = 'ar') {
  const d = a.published_date || a.created_at;
  if (!d) return '';
  const date = new Date(d);
  const now  = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  // Arabic relative dates
  if (lang === 'ar') {
    if (diff === 0) return 'اليوم';
    if (diff === 1) return 'منذ يوم';
    if (diff < 7)  return `منذ ${diff} أيام`;
    if (diff < 14) return 'منذ أسبوع';
    if (diff < 30) return 'منذ أسبوعين';
    if (diff < 60) return 'منذ شهر';
    return `منذ ${Math.floor(diff / 30)} أشهر`;
  }

  // Urdu relative dates
  if (lang === 'ur') {
    if (diff === 0) return 'آج';
    if (diff === 1) return '1 دن پہلے';
    if (diff < 7)  return `${diff} دن پہلے`;
    if (diff < 14) return '1 ہفتہ پہلے';
    if (diff < 30) return '2 ہفتے پہلے';
    if (diff < 60) return '1 مہینہ پہلے';
    return `${Math.floor(diff / 30)} مہینے پہلے`;
  }

  // English — use actual formatted date for clarity (e.g. "Sep 17, 2025")
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
export function extractCvBio(scholar,maxLen=200){if(scholar.bio)return scholar.bio;if(!scholar.cv_full_text)return '';const skip=['الرئيسية','التجاوز','تبديل','اتصل','روابط','للتبرع','Stripe','Square','English','اردو','© 20','www.'];const lines=(scholar.cv_full_text||'').split('\n').map(l=>l.trim()).filter(l=>l.length>30&&!skip.some(s=>l.includes(s))&&!l.startsWith('"'));const bio=(lines[0]||'').trim();return bio.length>maxLen?bio.slice(0,maxLen)+'…':bio;}
export function slugify(str){return(str||'').toLowerCase().replace(/\s+/g,'-').replace(/[^\w\u0600-\u06FF-]/g,'').slice(0,60);}